import { useQuery } from 'react-query'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { notify } from '@offerzen/react-utils'

import { errorUser as errorUserSelector } from '@/CandidateExperience/selectors/currentUser'
import { usePagination } from '@/CandidateExperience/hooks/usePagination'

import { fetchRecommendedJobs } from './api'

const PAGINATION_LIMIT = 20

/**
 * @typedef {Object} FetchPaginatedRecommendedJobsState
 * @property {Object} data - The recommended jobs data for the current page.
 * @property {boolean} isLoading
 * @property {boolean} isFetchError - Error fetching the recommended jobs.
 * @property {boolean} isPaginationError - Error calculating the total pages.
 * @property {number} currentPage - The current page number.
 * @property {(page: Number) => undefined} setPage - Sets the current page number.
 * @property {Number} totalPages - The total number of pages.
 * @property {Number} paginationLimit - Amount of items displayed per page.
 * @property {Number} paginationOffset - The position in the list of items where the current page starts.
 */

/**
 * Custom hook for fetching paginated recommended jobs.
 *
 * @param {Object} options
 * @param {string} options.candidateProfileId
 * @param {string} options.search
 * @returns {FetchPaginatedRecommendedJobsState} - The pagination state, actions, and helpers.
 */
const useFetchRecommendedJobs = ({ candidateProfileId, searchTerms }) => {
  const { currentPage, setPage, offset, calculateTotalPages } = usePagination({ limit: PAGINATION_LIMIT })

  const { data, isLoading, isError } = useQuery(['recommendedJobs', candidateProfileId, offset, searchTerms], () =>
    fetchRecommendedJobs({ candidateProfileId, offset, limit: PAGINATION_LIMIT, searchTerms }),
  )

  const errorUser = useSelector(state => errorUserSelector(state))

  const totalPages = calculateTotalPages(data?.recommendedPositionCards?.totalCount)

  useEffect(() => {
    if (currentPage > totalPages) {
      setPage(1)
    }
  }, [currentPage, setPage, totalPages])

  const state = {
    data: null,
    isLoading,
    isFetchError: isError,
    isPaginationError: false,
    currentPage,
    setPage,
    totalPages: null,
    paginationOffset: offset,
    paginationLimit: PAGINATION_LIMIT,
  }

  if (isError) {
    return {
      ...state,
      isFetchError: true,
    }
  }

  if (isLoading) {
    return {
      ...state,
      isLoading: true,
    }
  }

  if (!data || !data.recommendedPositionCards) {
    return {
      ...state,
      isFetchError: true,
    }
  }

  if (data.recommendedPositionCards.totalCount === null || data.recommendedPositionCards.totalCount === undefined) {
    notify(
      `Error: Pagination error while loading available jobs. Details: Likely caused by an invalid totalCount returned from recommendedPositionCards query (totalCount: ${data.recommendedPositionCards.totalCount})`,
      errorUser,
    )

    return {
      ...state,
      data: data.recommendedPositionCards,
      isPaginationError: true,
    }
  }

  return {
    ...state,
    data: data.recommendedPositionCards,
    totalPages,
  }
}

export default useFetchRecommendedJobs
